import React, { useState, useEffect } from "react";
import fetchWrapper from "../components/utility/fetchWrapper";
import { API_BASE_URL } from '../services/config.js';
import {
  Card,
  Typography,
  Table,
  Tag,
  Spin,
  message,
  Button,
  Space,
  Pagination,
  Select,
  Input,
  Tooltip,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useTheme } from "../context/ThemeContext";

const { Title, Text } = Typography;
const { Option } = Select;

const BASE_URL = `${API_BASE_URL}/api/v1/active-services`;

const timePresets = [
  { label: "Last 15 min", value: 15 },
  { label: "Last 30 min", value: 30 },
  { label: "Last 1 hour", value: 60 },
  { label: "Last 2 hours", value: 120 },
];

const durationPresets = [
  { label: "All durations", value: 0 },
  { label: "> 100ms", value: 100 },
  { label: "> 500ms", value: 500 },
  { label: "> 1s", value: 1000 },
  { label: "> 2s", value: 2000 },
];

const ActiveServicesTracesPage = () => {
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [serviceDetails, setServiceDetails] = useState({});
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [lookback, setLookback] = useState(60);
  const [minDuration, setMinDuration] = useState(0);
  const [serviceNameFilter, setServiceNameFilter] = useState(null);
  const [traceIdFilter, setTraceIdFilter] = useState("");

  const fetchTraces = async (
    pageNum = page,
    lookbackMinutes = lookback,
    minDurationMs = minDuration
  ) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        lookback_minutes: lookbackMinutes,
        min_duration_ms: minDurationMs,
        page: pageNum,
        limit,
        sort_by: "timestamp",
        sort_order: "desc",
      }).toString();

      const response = await fetchWrapper(`${BASE_URL}?${params}`);
      if (response.status === 404) {
        setServices([]);
        setServiceDetails({});
        setTotal(0);
        setError("No active services with traces found or endpoint not available.");
        return;
      }

      if (!response.ok) throw new Error("Failed to fetch traces");
      const data = await response.json();

      setServices(data.services || []);
      setTotal(data.total_services || 0);

      const grouped = {};
      (data.service_details || []).forEach((detail) => {
        if (!grouped[detail.service_name]) grouped[detail.service_name] = [];
        grouped[detail.service_name].push(detail);
      });

      setServiceDetails(grouped);
    } catch (err) {
      setError(err.message);
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    fetchTraces(1, lookback, minDuration);
    // eslint-disable-next-line
  }, [lookback, minDuration]);

  // Get all unique service names for filter dropdown
  const allServiceNames = services;

  // Filtering logic
  const getFilteredTraceData = (service) => {
    let data = (serviceDetails[service] || []).map((trace) => ({
      ...trace,
      service_name: service,
    }));
    if (traceIdFilter) {
      data = data.filter((trace) =>
        trace.latest_trace_id.toLowerCase().includes(traceIdFilter.toLowerCase())
      );
    }
    return data;
  };

  const filteredServices = serviceNameFilter
    ? services.filter((s) => s === serviceNameFilter)
    : services;

  const traceColumns = [
    {
      title: <span style={{ color: isDark ? "#e6edf3" : undefined }}>Service Name</span>,
      dataIndex: "service_name",
      key: "service_name",
      render: (name) => <Tag style={{ color: isDark ? '#adbac7' : undefined, background: isDark ? '#21262d' : undefined }}>{name}</Tag>,
    },
    {
      title: <span style={{ color: isDark ? "#e6edf3" : undefined }}>Trace ID</span>,
      dataIndex: "latest_trace_id",
      key: "trace_id",
      render: (id) => (
        <Space>
          <Text code style={{ color: isDark ? "#adbac7" : undefined, background: isDark ? "#0d1117" : undefined }}>{id}</Text>
          <Button
            type="text"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(id);
                message.success("Copied!");
              } catch {
                message.error("Failed to copy");
              }
            }}
            style={{ 
              color: "#1890ff",
            }}
          >
            Copy
          </Button>
        </Space>
      ),
    },
    {
      title: <span style={{ color: isDark ? "#e6edf3" : undefined }}>Timestamp</span>,
      dataIndex: "latest_timestamp",
      key: "timestamp",
      render: (ts) => <Text style={{ color: isDark ? "#adbac7" : undefined }}>{ts}</Text>,
    },
    {
      title: <span style={{ color: isDark ? "#e6edf3" : undefined }}>Duration</span>,
      dataIndex: "duration_readable",
      key: "duration",
      render: (d) => <Tag color="blue">{d}</Tag>,
    },
    {
      title: <span style={{ color: isDark ? "#e6edf3" : undefined }}>View Trace</span>,
      dataIndex: "grafana_url",
      key: "grafana_url",
      render: (url) =>
        url ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-block",
              width: "110px",
              height: "44px",
              backgroundColor: "#1890ff",
              color: "white",
              borderRadius: "12px",
              textAlign: "center",
              lineHeight: "20px",
              fontWeight: 500,
              gap: "8px",
              opacity: 1,
              alignContent: "center",
              boxShadow: isDark ? "0 2px 8px rgba(24, 144, 255, 0.3)" : "-8px -8px 16px 0px #FFFFFF, 8px 8px 16px 0px #C9D9E8"
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = "#40a9ff"}
            onMouseLeave={(e) => e.target.style.backgroundColor = "#1890ff"}
          >
            View Trace
          </a>
        ) : (
          <span style={{ color: isDark ? "#6e7681" : "#aaa" }}>-</span>
        ),
    },
  ];

  return (
    <div style={{ 
      padding: "var(--ant-padding-lg)",
      maxWidth: "100%",
      width: "100%",
      boxSizing: "border-box"
    }}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          marginBottom: "var(--ant-margin-lg)",
          gap: "var(--ant-margin-md)",
        }}
      >
        <Title level={2} style={{ margin: 0, color: isDark ? "#e6edf3" : undefined }}>
          Active Service Traces
        </Title>
        <Space>
          <Tooltip title="Refresh">
            <Button 
              icon={<ReloadOutlined />}
              onClick={() => {
                setPage(1);
                fetchTraces(1, lookback, minDuration);
              }}
              loading={loading}
              style={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            />
          </Tooltip>
          <Select
            value={lookback}
            onChange={(value) => setLookback(value)}
            style={{ 
              width: 140,
              background: isDark ? '#0d1117' : undefined,
              borderColor: isDark ? '#30363d' : undefined,
            }}
            dropdownStyle={{ background: isDark ? '#161b22' : undefined }}
          >
            {timePresets.map((preset) => (
              <Option key={preset.value} value={preset.value}>
                {preset.label}
              </Option>
            ))}
          </Select>
          <Select
            value={minDuration}
            onChange={(value) => setMinDuration(value)}
            style={{ 
              width: 120,
              background: isDark ? '#0d1117' : undefined,
              borderColor: isDark ? '#30363d' : undefined,
            }}
            dropdownStyle={{ background: isDark ? '#161b22' : undefined }}
          >
            {durationPresets.map((preset) => (
              <Option key={preset.value} value={preset.value}>
                {preset.label}
              </Option>
            ))}
          </Select>
          <Select
            allowClear
            placeholder="Service Name"
            value={serviceNameFilter}
            onChange={setServiceNameFilter}
            style={{ 
              width: 160,
              background: isDark ? '#0d1117' : undefined,
              borderColor: isDark ? '#30363d' : undefined,
            }}
            dropdownStyle={{ background: isDark ? '#161b22' : undefined }}
          >
            {allServiceNames.map((name) => (
              <Option key={name} value={name}>
                {name}
              </Option>
            ))}
          </Select>
          <Input
            placeholder="Search Trace ID"
            value={traceIdFilter}
            onChange={(e) => setTraceIdFilter(e.target.value)}
            style={{ 
              width: 180,
              background: isDark ? '#0d1117' : undefined,
              borderColor: isDark ? '#30363d' : undefined,
              color: isDark ? '#e6edf3' : undefined,
            }}
            allowClear
          />
        </Space>
      </div>

      {loading ? (
        <div style={{ 
          display: "flex", 
          justifyContent: "center", 
          alignItems: "center",
          padding: "var(--ant-padding-xl)",
          minHeight: "var(--ant-control-height-xl)"
        }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <Text type="danger" style={{ color: isDark ? "#f85149" : undefined }}>{error}</Text>
      ) : filteredServices.length > 0 ? (
        <>
          {filteredServices.map((service) => {
            const traceData = getFilteredTraceData(service);

            return (
              <Table
                key={service}
                className="custom-ant-table rounded-rows-table"
                columns={traceColumns}
                dataSource={traceData}
                rowKey="latest_trace_id"
                pagination={false}
                locale={{ emptyText: "No traces found for this service." }}
                size="middle"
                style={{ 
                  marginBottom: "var(--ant-margin-lg)",
                  background: isDark ? '#161b22' : undefined,
                }}
              />
            );
          })}
          <div style={{ 
            textAlign: "center", 
            marginTop: "var(--ant-margin-lg)",
            marginBottom: "var(--ant-margin-lg)"
          }}>
            <Pagination
              current={page}
              pageSize={limit}
              total={total}
              simple
              showSizeChanger={false}
              showTotal={(total) => <span style={{ color: isDark ? '#adbac7' : undefined }}>Total {total} items</span>}
              onChange={(p) => {
                setPage(p);
                fetchTraces(p, lookback, minDuration);
              }}
            />
          </div>
        </>
      ) : (
        <Text type="secondary" style={{ color: isDark ? "#adbac7" : undefined }}>
          No active services with traces found.
        </Text>
      )}
    </div>
  );
};

export default ActiveServicesTracesPage;